import { Component, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {Store} from '../../../models/store';
import {ActivatedRoute} from '@angular/router';
import {environment} from '../../../../environments/environment';
import {OrderService} from '../../../order.service';
import {NgNavigatorShareService} from 'ng-navigator-share';
import {MatSnackBar} from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

@Component({
  selector: 'app-send-gift-card',
  templateUrl: './send-gift-card.component.html',
  styleUrls: ['./send-gift-card.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SendGiftCardComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  store: Store;
  hash: string;
  phoneNumber: string;
  offerCatalogSharableLink: string;
  storeId: string;
  isLoading: boolean = false;
  myOffers: any[];
  phone_number_verified: boolean = false;
  ngNavigatorShareService: NgNavigatorShareService;

  // ENG-5034: per-card inline gift form state
  activeOffer: any = null;
  giftForm = this.blankForm();
  sending = false;
  sentTo: { [offerOrderId: string]: string } = {};

  constructor(private route: ActivatedRoute,
              private orderService: OrderService,
              ngNavigatorShareService: NgNavigatorShareService,
              private snackBar: MatSnackBar,
              private cdr: ChangeDetectorRef) {

    this.ngNavigatorShareService = ngNavigatorShareService;

    this.hash = this.route.snapshot.paramMap.get('hash');
    this.phoneNumber = localStorage.getItem('phoneNumber');
    this.storeId = this.route.snapshot.paramMap.get('storeId');

    this.orderService.getStoreInfo(this.storeId).pipe(takeUntil(this.destroy$)).subscribe(store => {
      this.store = Object.assign(new Store(), store);
      this.cdr.markForCheck();
    });

    if(this.hash){
      this.orderService.getMyOffersWithHash(this.storeId, this.hash).pipe(takeUntil(this.destroy$)).subscribe(response => {
        this.myOffers = Array.isArray(response) ? response : ((response as any).my_offers || []);
        this.phone_number_verified = true;
        this.isLoading = false;
        this.cdr.markForCheck();
      });
    }else{
      if(localStorage.getItem('phoneNumber') !== null){
        this.phone_number_verified = true;
        this.phoneNumber = localStorage.getItem('phoneNumber')
      }

      if(this.phone_number_verified){
        this.orderService.getMyOffers(this.phoneNumber).pipe(takeUntil(this.destroy$)).subscribe(response => {
          this.myOffers = Array.isArray(response) ? response : ((response as any).my_offers || []);
          this.isLoading = false;
          this.cdr.markForCheck();
        })
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private blankForm() {
    return {
      recipient_name: '',
      delivery_method: 'SMS' as 'SMS' | 'EMAIL',
      recipient_phone: '',
      recipient_email: '',
      sender_name: '',
      message: '',
      send_timing: 'now' as 'now' | 'later',
      scheduled_date: '',
      scheduled_time: ''
    };
  }

  get minDate(): string {
    return new Date().toISOString().split('T')[0];
  }

  setDeliveryMethod(method: 'SMS' | 'EMAIL') {
    this.giftForm.delivery_method = method;
    this.cdr.markForCheck();
  }

  openGiftForm(offer) {
    this.activeOffer = offer;
    this.giftForm = this.blankForm();
    this.cdr.markForCheck();
  }

  closeGiftForm() {
    this.activeOffer = null;
    this.cdr.markForCheck();
  }

  private plainPhone(phoneNumber: string) {
    let digits = (phoneNumber || '').replace(/\D/g, '');
    if (digits.length === 11 && digits.startsWith('1')) {
      digits = digits.substring(1);
    }
    return digits.length >= 10 ? '+1' + digits.substring(0, 10) : '';
  }

  get phoneIncomplete(): boolean {
    return !!this.giftForm.recipient_phone && this.plainPhone(this.giftForm.recipient_phone) === '';
  }

  private isEmailValid(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || '').trim());
  }

  get emailIncomplete(): boolean {
    return !!this.giftForm.recipient_email && !this.isEmailValid(this.giftForm.recipient_email);
  }

  isGiftFormValid(): boolean {
    const v = this.giftForm;
    if (!v.recipient_name.trim() || !v.sender_name.trim()) return false;
    if (v.delivery_method === 'EMAIL') {
      if (!this.isEmailValid(v.recipient_email)) return false;
    } else {
      if (this.plainPhone(v.recipient_phone).length !== 12) return false;
    }
    if (v.send_timing === 'later' && (!v.scheduled_date || !v.scheduled_time)) return false;
    return true;
  }

  sendGift(offer) {
    if (this.sending || !this.isGiftFormValid()) return;
    this.sending = true;
    const v = this.giftForm;
    const details: any = {
      recipient_name: v.recipient_name.trim(),
      sender_name: v.sender_name.trim(),
      message: v.message.trim(),
      delivery_method: v.delivery_method,
      recipient_phone: v.delivery_method === 'SMS' ? this.plainPhone(v.recipient_phone) : '',
      recipient_email: v.delivery_method === 'EMAIL' ? v.recipient_email.trim() : ''
    };
    if (v.send_timing === 'later' && v.scheduled_date && v.scheduled_time) {
      details.scheduled_at = new Date(`${v.scheduled_date}T${v.scheduled_time}`).toISOString();
    }
    const offerOrderId = offer.offer_order.offer_order_id;
    this.orderService.sendGift(offerOrderId, details).pipe(
      finalize(() => { this.sending = false; this.cdr.markForCheck(); })
    ).subscribe(() => {
      this.sentTo[offerOrderId] = details.recipient_name;
      this.activeOffer = null;
      this.cdr.markForCheck();
    }, () => {
      this.snackBar.open('We could not send the gift. Please try again.', 'OK', {
        duration: 5000,
        verticalPosition: 'top'
      });
    });
  }

  shareLink(offer) {
    const url = `${environment.hostName}/gift/${offer.offer_order.offer_order_id}/`
    if (!this.ngNavigatorShareService.canShare()) {
      this.copyMessage(url);
      return;
    }
    this.ngNavigatorShareService.share({
      title: 'I got this for you!',
      text: 'I got this for you!\n\n',
      url: url
    }).catch((error) => {
      console.log(error);
    });
  }

  copyMessage(val: string){
    navigator.clipboard.writeText(val).then(() => {
      this.snackBar.open('Link copied to clipboard - share link with the new owner!', "OK", {
        duration: 5000,
        verticalPosition: 'top'
      });
    });
  }


}
